import { defineStore } from 'pinia';
import { getControllers, getAllModulesDetails, getOneModuleDetail, getAllScripts, getCurrentSession, getEventsList, pauseTimer } from '@/services/DesktopAgent';
import { getAllOperatorViews, getUsersViewsPreferences } from '@/services/ShowOperatorService';
import { BATTERY_STRENGTH_INDICATOR, CONTINUITY_STATUS, LAYOUTS, MODULE_TYPES, SIGNAL_STRENGTH_INDICATOR } from '@/constants';
import moment from 'moment';
import { toast } from 'vue-sonner';
import { useShowStore } from './show';
import _ from 'lodash';
import { DEFAULT_SHOW_OPERATOR_DISPLAY_SETTINGS, STANDARD_VIEWS, STANDARD_EVENT_LIST_VIEWS } from '@/constants/ShowOperatorConstants';
import { useLayoutStore } from './layout';
import { DEFAULT_EVENT_DISPLAY_SETTINGS } from '@/constants/EventDisplayConstants';
import { createStepParentChildRelationship } from '@/helpers/eventsHelper';
import { markRaw } from 'vue';

export const useShowOperatorStore = defineStore('showOperatorStore', {
	state: () => ({
		is_connected_to_controller: false,
		connected_controller: undefined,
		previously_connected_controller: undefined,
		controllers: [],
		modules: [],
		all_custom_views: [],
		standard_views_users_views_preferences: undefined,

		is_loading_all_modules: false,

		scripts: [],
		current_script: undefined,
		current_script_and_active_show_mismatched: false,

		total_6M_modules: 0,
		total_18M_modules: 0,
		total_36M_modules: 0,
		total_72M_modules: 0,
		total_90M_modules: 0,
		total_audioboxes: 0,
		device_info: undefined,
		device_status: undefined,
		selected_module: undefined,
		all_modules: [],
		current_loaded_view: {
			is_custom_view: false,
			id: undefined,
		},
		all_controllers: [],
		previous_route_before_device_modal: undefined,

		search: undefined,
		search_type: 'event_description',
		filters: undefined,
		current_view_filters: undefined,

		current_session: undefined,

		current_view_display_settings: undefined,
		main_ui_filters: undefined,

		open_script_attributes_modal: false,
		is_user_editing_new_custom_view: false,

		//Operator Control Specific states
		events_list: [],
		event_search: undefined,
		event_search_type: 'event_description',

		controls_disable_firing_groups: undefined,

		show_controls_buttons_locked: false,
		pyro_events: [],
		channel_usage: {},

		current_ticker_time: Date.now(),
		timers: {},

		is_loading_all_events: false,
		all_events: [],

		// Internal cache for display_settings getter stabilization
		_cached_display_settings: undefined,
		_cached_display_settings_key: '',
	}),
	getters: {
		display_settings: function (state) {
			let settings = useLayoutStore().current_layout == LAYOUTS?.OPERATOR_CONTROLS?.key ? DEFAULT_EVENT_DISPLAY_SETTINGS : DEFAULT_SHOW_OPERATOR_DISPLAY_SETTINGS;

			if (state.current_view_display_settings != undefined) {
				settings = { ...settings, ...state.current_view_display_settings };
			}

			if (state.is_user_editing_new_custom_view != true && state.current_loaded_view?.users_views_preferences?.display_settings != undefined) {
				settings = { ...settings, ...state.current_loaded_view.users_views_preferences.display_settings };
			}

			// Stabilize the reference: only return a new object when the computed result actually changed
			const key = JSON.stringify(settings);
			if (state._cached_display_settings_key === key) {
				return state._cached_display_settings;
			}
			state._cached_display_settings_key = key;
			state._cached_display_settings = settings;
			return settings;
		},

		missing_channels: function (state) {
			let unique_channels_used_in_script = new Set();
			if (state.current_script != undefined) {
				for (const event of state.current_script.pyro_events) {
					if (event.channel == undefined) {
						continue;
					}
					unique_channels_used_in_script.add(event.channel);
				}
			}

			let unique_channels_found = new Set();
			for (const main_module of state.all_modules) {
				for (const bank of main_module.banks) {
					if (bank.channel != undefined) {
						unique_channels_found.add(bank.channel);
					}
				}
			}

			let missing_channels = [...unique_channels_used_in_script].filter((channel) => !unique_channels_found.has(channel));
			return missing_channels.map((channel) => {
				return {
					channel,
					events: state.current_script?.pyro_events?.filter((event) => event.channel == channel) ?? [],
				};
			});
		},
		timer_list: function (state) {
			return Object.values(state.timers).sort((a, b) => new Date(a.created_on) - new Date(b.created_on));
		},
		inapplicable_event_fields: function (state) {
			const inapplicable_event_fields = new Set();

			if (!(state.current_script?.DMX_event_count > 0)) {
				inapplicable_event_fields.add('dmx_universe');
			}

			const events = state.all_events;
			if (events.length === 0) return inapplicable_event_fields;

			if (!events.some((event) => event.position != null && event.position !== '')) {
				inapplicable_event_fields.add('position');
			}

			if (!events.some((event) => event.igniter_pre_fire_in_seconds > 0 || event.igniter_pre_fire > 0)) {
				inapplicable_event_fields.add('pre_fire');
			}

			if (!events.some((event) => event.lift_time_in_seconds > 0 || event.lift_time > 0)) {
				inapplicable_event_fields.add('lift_time');
			}

			if (!events.some((event) => event.pulse > 0)) {
				inapplicable_event_fields.add('pulse_time');
			}

			if (!events.some((event) => event.disable_firing_group_id != null)) {
				inapplicable_event_fields.add('disable_group');
			}

			return inapplicable_event_fields;
		},
	},
	actions: {
		resetEnvironment: function () {
			console.log('Resetting show operator store environment');
			this.is_connected_to_controller = false;
			this.connected_controller = undefined;
			this.previously_connected_controller = undefined;
			this.connectors = [];
			this.modules = [];
			this.scripts = [];
			this.current_script = undefined;

			this.total_6M_modules = 0;
			this.total_18M_modules = 0;
			this.total_36M_modules = 0;
			this.total_72M_modules = 0;
			this.total_90M_modules = 0;
			this.total_audioboxes = 0;
			this.device_info = undefined;
			this.device_status = undefined;
			this.selected_module = undefined;
			this.all_modules = [];
			this.all_controllers = [];

			this.refreshAllData();
		},

		refreshControllerData: async function () {
			const controllers_info = await getControllers();
			this.all_controllers = controllers_info;
			console.log('controller response', controllers_info);
			if (controllers_info == undefined) {
				// so if multiple requests are polled we don't lose the last connected controller
				if (this.previously_connected_controller != undefined) {
					return;
				}

				this.previously_connected_controller = this.connected_controller;
				this.previously_connected_controller.status = 'DISCONNECTED';
				this.previously_connected_controller.lost_connection_at = moment().toISOString();
				this.is_connected_to_controller = false;
				this.connected_controller = undefined;
				this.device_info = {};
				this.device_status = {};
				return;
			}

			let controller = controllers_info.find((controller) => controller.status == 'CONNECTED');

			if (controller == undefined && this.connected_controller != undefined) {
				this.previously_connected_controller = this.connected_controller;
				this.previously_connected_controller.status = 'DISCONNECTED';
				this.previously_connected_controller.lost_connection_at = moment().toISOString();
			} else if (controller != undefined && this.previously_connected_controller != undefined && controller?.mac_address == this.previously_connected_controller?.mac_address) {
				this.previously_connected_controller = undefined;
			}

			if (controller == undefined && this.previously_connected_controller != undefined) {
				toast(`${this.previously_connected_controller.device_model} controller was disconnected.`);
			} else if (controller != undefined && this.connected_controller?.mac_address != controller?.mac_address) {
				toast(`${controller.device_model} controller was connected.`);
			}

			this.connected_controller = controller;
			this.is_connected_to_controller = this.connected_controller != undefined;
			this.device_info = {};
			this.device_status = {};

			if (this.is_connected_to_controller == false) {
				return;
			}

			this.device_info.number_of_script_stored_in_memory = this.connected_controller.number_of_script_stored_in_memory;
			this.device_info.mac_address = this.connected_controller.mac_address;

			this.device_status.battery_level = this.connected_controller.battery_level;
		},
		refreshModulesData: _.debounce(async function () {
			if (this.is_connected_to_controller == false) {
				this.modules = [];
				this.receivedModules([]);
				return;
			}

			let module_details = [];
			module_details = await getAllModulesDetails({ controller_mac_address: this.connected_controller.mac_address, filters: [...(this.current_view_filters ?? []), ...(this.filters ?? [])], search: this.search, search_type: this.search_type });
			const all_modules = await getAllModulesDetails({ controller_mac_address: this.connected_controller.mac_address });
			console.log('modules response', module_details);
			if (module_details == undefined) {
				this.modules = [];
				this.receivedModules([]);
				return;
			}

			this.modules = module_details;
			this.createDisableGroupMapping(this.modules);
			this.receivedModules(this.modules, all_modules);
			console.log('selected module updated 1', module_details, this.selected_module);

			// if module details modal is open update it in real time
			if (this.selected_module != undefined) {
				const module_index = this.modules.findIndex((module) => module.module.module_id === this.selected_module.module.module_id);
				console.log('selected module updated 3', module_index);

				if (module_index !== -1) {
					this.selected_module = this.modules[module_index];
					console.log('selected module updated 2', this.selected_module);
				}
			}
		}, 500),

		refreshSingleModuleData: debounceSameModuleID(async function ({ module_id }) {
			// console.log('refreshSingleModuleData', module_id, this.is_connected_to_controller);
			if (module_id == undefined) {
				return;
			}
			if (this.is_connected_to_controller == false) {
				return;
			}

			const module_details = await getOneModuleDetail({ module_id, controller_mac_address: this.connected_controller.mac_address, filters: [...(this.current_view_filters ?? []), ...(this.filters ?? [])], search: this.search, search_type: this.search_type });
			// console.log('single modules response', module_details);

			if (module_details == undefined) {
				const module_index = this.modules.findIndex((module) => module.module.parent_module_id === module_id);
				if (module_index !== -1) {
					delete this.modules[module_index];
				}
				return;
			}

			const module_index = this.modules.findIndex((module) => module.module.parent_module_id === module_id);
			if (module_index !== -1) {
				this.modules[module_index] = module_details;
			} else {
				this.modules.push(module_details);
			}
			this.createDisableGroupMapping(this.modules);
			this.receivedModules(this.modules);
			// console.log('selected module updated 0', this.selected_module != undefined && this.selected_module.module.parent_module_id == module_id);

			// if module details modal is open update it in real time
			if (this.selected_module != undefined && this.selected_module.module.parent_module_id == module_id) {
				this.selected_module = module_details;
				// console.log('selected module updated', this.selected_module);
			}
		}, 500),

		refreshScriptData: async function () {
			if (this.is_connected_to_controller == false) {
				this.scripts = [];
				this.current_script = undefined;
				return;
			}

			const scripts = await getAllScripts({ controller_mac_address: this.connected_controller.mac_address });
			console.log('scripts response', scripts);
			if (scripts == undefined) {
				this.scripts = [];
				this.current_script = undefined;
				return;
			}

			this.scripts = scripts;
			this.current_script = this.scripts[0];
		},

		refreshAllData: async function () {
			await this.getSessionData();
			await this.refreshControllerData();
			this.refreshModulesData();
			await this.refreshScriptData();
			await this.getEventsList();
		},

		sortShowOperatorViews: function (views) {
			if (views == undefined) {
				views = this.all_custom_views;
			}

			views = views.sort((a, b) => {
				let a_sort_order = a?.users_views_preferences?.sort_order ?? Number.MAX_SAFE_INTEGER;
				let b_sort_order = b?.users_views_preferences?.sort_order ?? Number.MAX_SAFE_INTEGER;
				return a_sort_order - b_sort_order;
			});

			return views;
		},
		getShowOperatorViews: async function () {
			let view_type = 'DEVICE';
			if (useLayoutStore().current_layout == LAYOUTS?.OPERATOR_CONTROLS?.key) {
				view_type = 'CONTROL';
			}
			const response = await getAllOperatorViews(view_type);

			if (response.error != undefined) {
				toast.error('Error loading views.');
				return;
			}

			let views = response ?? [];
			views = views.map((view) => ({ ...view, is_custom_view: true }));

			views = this.sortShowOperatorViews(views);

			this.all_custom_views = views;

			for (const view of this.all_custom_views) {
				if (view.id == this.current_loaded_view.id) {
					this.current_loaded_view = view;
					this.current_view_filters = view.filters;
					this.current_view_display_settings = view.display_settings;
					break;
				}
			}

			return this.all_custom_views;
		},

		getStandardViewsUsersViewsPreferences: async function () {
			let standard_view_ids = useLayoutStore().current_layout == LAYOUTS?.OPERATOR_CONTROLS?.key ? STANDARD_EVENT_LIST_VIEWS.map((view) => view.id) : STANDARD_VIEWS.map((view) => view.id);

			const response = await getUsersViewsPreferences({ view_ids: standard_view_ids });

			if (response.error != undefined) {
				console.log('Error loading standard views users preferences', response);
				return;
			}

			this.standard_views_users_views_preferences = response;
			return this.standard_views_users_views_preferences;
		},

		// setAllOperatorViews(views) {
		// 	this.all_custom_views = views;
		// },

		// addNewCustomView(view) {
		// 	this.all_custom_views.push(view);
		// },

		receivedModules(modules, all_modules) {
			// console.log('module info here', modules);
			this.total_6M_modules = 0;
			this.total_18M_modules = 0;
			this.total_36M_modules = 0;
			this.total_72M_modules = 0;
			this.total_90M_modules = 0;
			this.total_audioboxes = 0;
			this.modules = modules;
			this.all_modules = all_modules ?? modules;
			for (const module_detail of this.all_modules) {
				if (module_detail.module.module_type == MODULE_TYPES.THIRTY_SIX_M_MAIN) {
					this.total_36M_modules++;
				} else if (module_detail.module.module_type == MODULE_TYPES.SEVENTY_TWO_M_MAIN) {
					this.total_72M_modules++;
				} else if (module_detail.module.module_type == MODULE_TYPES.EIGHTEEN_M) {
					this.total_18M_modules++;
				} else if (module_detail.module.module_type == MODULE_TYPES.NINETY_M_MAIN) {
					this.total_90M_modules++;
				} else if (module_detail.module.module_type == MODULE_TYPES.SIX_M) {
					this.total_6M_modules++;
				} else if (module_detail.module.module_type == MODULE_TYPES.AUDIOBOX) {
					this.total_audioboxes++;
				}
			}
		},
		selectAModule(module) {
			this.selected_module = module;
		},
		showExceptionModules() {
			let filtered_modules = [];

			for (let i = 0; i < this.all_modules.length; i++) {
				const module_data = this.all_modules[i];

				if (module_data.module.signal_strength_level > SIGNAL_STRENGTH_INDICATOR.BAD) {
					filtered_modules.push(module_data);
					continue;
				} else if (module_data.module.battery_1_level <= BATTERY_STRENGTH_INDICATOR.BAD || module_data.module.battery_2_level <= BATTERY_STRENGTH_INDICATOR.BAD) {
					filtered_modules.push(module_data);
					continue;
				}
				let has_bad_continuity = false;

				for (const bank of module_data.banks) {
					for (const continuity_key in bank.continuity) {
						const continuity_data = bank.continuity[continuity_key];
						if (continuity_data.continuity_status !== CONTINUITY_STATUS.UNUSED && continuity_data.continuity_status !== CONTINUITY_STATUS.GOOD) {
							has_bad_continuity = true;
							break;
						}
					}
					if (has_bad_continuity) break;
				}

				if (has_bad_continuity) {
					filtered_modules.push(module_data);
				}
			}
			this.modules = filtered_modules;
		},

		setStandardViewFilter(view) {
			// this.current_loaded_view.id = view.id;
			this.current_loaded_view = view;
			this.current_loaded_view.is_custom_view = false;
			this.current_view_filters = view.filters;
		},
		applyCustomView(view, is_operator_controls_layout = false) {
			// this.current_loaded_view.id = view.id;
			this.current_loaded_view = view;
			this.current_loaded_view.is_custom_view = true;
			if (is_operator_controls_layout) {
				this.getEventsList();
			} else {
				this.refreshModulesData();
			}
		},

		pushToFilteredModules(filtered_modules, main_module) {
			if (filtered_modules.findIndex((existing_module) => existing_module.module.module_id === main_module.module.module_id) === -1) {
				filtered_modules.push(main_module);
			}
		},

		storePreviousRouteOnDeviceModalOpen(route) {
			this.previous_route_before_device_modal = route;
		},
		createDisableGroupMapping(modules) {
			const show_store = useShowStore();
			if (show_store?.active_show?.disable_firing_groups == undefined || show_store?.active_show?.disable_firing_groups.length == 0) {
				return;
			}

			let disable_groups_by_name = {};
			for (const disable_firing_group of show_store.active_show.disable_firing_groups) {
				disable_groups_by_name[disable_firing_group.label.toUpperCase().trim()] = disable_firing_group;
			}

			if (show_store.active_show != undefined && this.current_script != undefined && this.current_script_and_active_show_mismatched == false) {
				for (const module of modules) {
					for (const bank of module.banks) {
						for (const continuity_key in bank.continuity) {
							for (const event of bank.continuity[continuity_key].events) {
								if (event.disable_firing_group != undefined) {
									event.disable_firing_group.color = disable_groups_by_name[event.disable_firing_group.name]?.color;
								}
							}

							for (const disable_firing_group of bank.continuity[continuity_key].disable_firing_groups) {
								disable_firing_group.color = disable_groups_by_name[disable_firing_group.name]?.color;
							}
						}

						for (const event of bank.events) {
							if (event.disable_firing_group != undefined) {
								event.disable_firing_group.color = disable_groups_by_name[event.disable_firing_group.name]?.color;
							}
						}
					}
				}
			}
		},

		getSessionData: async function () {
			this.current_session = await getCurrentSession();
			console.log('current_session response', this.current_session);
		},
		async getEventsList() {
			if (this.is_connected_to_controller == false || this.current_script == undefined) {
				this.all_events = [];
				this.events_list = [];
				return;
			}
			this.is_loading_all_events = true;
			const all_events = await getEventsList({ controller_mac_address: this.connected_controller.mac_address, script_id: this.current_script?.script_id });
			this.is_loading_all_events = false;
			this.all_events = markRaw([...all_events.pyro_events, ...all_events.dmx_events, ...all_events.alternate_pyro_events]);
			if (this.all_events.length == 0) {
				this.events_list = [];
				return;
			}
			const received_events = await getEventsList({ controller_mac_address: this.connected_controller.mac_address, script_id: this.current_script?.script_id, search_type: this.event_search_type, search: this.event_search, filters: [...(this.current_view_filters ?? []), ...(this.filters ?? [])] });
			if (received_events.error != undefined) return;
			this.pyro_events = markRaw([...received_events.pyro_events]);
			received_events.dmx_events.forEach((event) => (event.duration_in_seconds = event.duration / 100));
			this.initializeChannelUsage();
			this.events_list = markRaw([...received_events.pyro_events, ...received_events.dmx_events, ...received_events.alternate_pyro_events]);
			this.controls_disable_firing_groups = received_events.disable_firing_groups;
			createStepParentChildRelationship({ start_from_row_index: 0, is_control_view: true });
			for (let i = 0; i < this.events_list.length; i++) {
				const event = this.events_list[i];
				if (event.event_type == 'STEP') {
					const previous_event_time = this.events_list[i - 1]?.event_time ?? 0;
					event.event_time = previous_event_time;
				} else if (event.parent_event != undefined) {
					event.display_event_time = event.event_time;
					event.event_time = this.events_list.find((found_event) => found_event.event_id == event.parent_event).event_time + event.event_time;
				}
			}
		},
		mapDurationAndPrefireToEventsList(active_show) {
			if (this.events_list == undefined || this.events_list.length == 0) return;
			const duration_map = new Map(active_show.events.map((e) => [e.event_id, { duration_in_seconds: e.duration_in_seconds, igniter_pre_fire_in_seconds: e.igniter_pre_fire_in_seconds, lift_time_in_seconds: e.lift_time_in_seconds }]));
			this.events_list = this.events_list.map((event) => ({
				...event,
				...duration_map.get(event.event_id.toString()),
			}));
		},
		updateChannelUsage(event_index) {
			let any_changed = false;

			for (const pyro_event of this.pyro_events) {
				if (pyro_event.controller_event_id >= event_index) continue;
				for (const cue of pyro_event.cues) {
					if (!this.channel_usage[pyro_event.channel]?.[cue]?.has_been_fired) {
						any_changed = true;
						break;
					}
				}
				if (any_changed) break;
			}

			if (!any_changed) return;

			const updated = {};
			for (const channel_id in this.channel_usage) {
				updated[channel_id] = {};
				for (const cue_id in this.channel_usage[channel_id]) {
					const cue = this.channel_usage[channel_id][cue_id];
					updated[channel_id][cue_id] = {
						events: cue.events,
						has_been_fired: false,
					};
				}
			}

			for (const pyro_event of this.pyro_events) {
				for (const cue of pyro_event.cues) {
					if (updated[pyro_event.channel]?.[cue]) {
						updated[pyro_event.channel][cue].has_been_fired = pyro_event.controller_event_id < event_index;
					}
				}
			}

			this.channel_usage = updated;
		},
		initializeChannelUsage(elapsed_time = 0) {
			const channel_usage = {};

			for (const pyro_event of this.pyro_events) {
				if (channel_usage[pyro_event.channel] == undefined) {
					channel_usage[pyro_event.channel] = {};
					for (let i = 1; i <= 18; i++) {
						channel_usage[pyro_event.channel][i] = {
							has_been_fired: false,
							events: [],
						};
					}
				}
				for (const cue of pyro_event.cues) {
					channel_usage[pyro_event.channel][cue].events.push(pyro_event);
					channel_usage[pyro_event.channel][cue].has_been_fired = pyro_event.event_time < elapsed_time;
				}
			}

			this.channel_usage = channel_usage;
		},
		tick() {
			this.current_ticker_time = Date.now();
			this.timer_list.forEach((timer) => {
				if (timer.type !== 'CONTINUOUS_CLOCK' && timer.status === 'RUNNING') {
					if (timer.type == 'ELAPSED_TIME' && (timer.elapsed_end_duration == undefined || timer.elapsed_end_duration == 0)) {
						return;
					}
					if (timer.end_at - this.current_ticker_time < 0 && !timer.allow_overrun) {
						pauseTimer(timer.id);
					}
				}
			});
		},
		applyTimerUpdate(timer) {
			this.timers[timer.id] = timer;
		},
		deleteTimer(timer_id) {
			delete this.timers[timer_id];
		},
	},
});

function debounceSameModuleID(func, wait) {
	const timeout_map = new Map(); // Map<module_id, timeout_id>

	return function (...args) {
		const current_args = args[0];
		const current_module_id = current_args?.module_id;
		// console.log('debounceSameModuleID', current_args, timeout_map.has(current_module_id));

		if (current_module_id == null) {
			// If no module_id, execute immediately
			func.apply(this, args);
			return;
		}

		// Clear existing timeout for this module_id if it exists
		if (timeout_map.has(current_module_id)) {
			clearTimeout(timeout_map.get(current_module_id));
		}

		// Set new timeout for this module_id
		const timeout_id = setTimeout(() => {
			timeout_map.delete(current_module_id);
			func.apply(this, args);
		}, wait);

		timeout_map.set(current_module_id, timeout_id);
	};
}
